import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { ArrowLeft, ArrowRight, CheckCircle2, Download, FileSignature, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase OTP-2 — Self-service OTP intake wizard.
 *
 * Route: /intake/otp  (also handled at /intake?type=conveyancing&action=otp)
 *
 * Flow:
 *   Step 1 — Capture lead (full_name, email, phone) → /matter/bootstrap creates
 *            Client + Matter + portal_token. NO ANONYMOUS DOWNLOADS — this is
 *            the gatekeeper for every subsequent step.
 *   Step 2 — Purchaser + Seller party details.
 *   Step 3 — Property details (freehold / sectional title / agricultural).
 *   Step 4 — Purchase price + bond/cash + occupation.
 *   Step 5 — (Optional) Annexure A defects disclosure.
 *   Step 6 — Generate + download.
 *
 * Calculator deeplinks pre-fill price + property_type via query params.
 */
const STEPS = [
  { idx: 0, label: "You" },
  { idx: 1, label: "Parties" },
  { idx: 2, label: "Property" },
  { idx: 3, label: "Price & terms" },
  { idx: 4, label: "Defects" },
  { idx: 5, label: "Generate" },
];

export default function OtpIntake() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const presetPrice = params.get("price") || "";
  const presetType  = params.get("property_type") || "freehold";

  const [step, setStep]     = useState(0);
  const [busy, setBusy]     = useState(false);
  const [portal, setPortal] = useState(null);  // {portal_token, matter_id}
  const [done, setDone]     = useState(false);
  const [bundleReady, setBundleReady] = useState(false);

  // Step 1 — lead
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  // Step 2 — parties
  const [purchaser, setPurchaser] = useState({ full_name: "", id_number: "", marital_status: "unmarried", address: "", email: "", phone: "" });
  const [seller,    setSeller]    = useState({ full_name: "", id_number: "", marital_status: "unmarried", address: "", email: "", phone: "" });
  // Step 3 — property
  const [property, setProperty] = useState({
    property_type: presetType,
    address: "", erf_number: "", township: "", province: "Gauteng", extent_m2: "", title_deed_number: "",
    section_number: "", scheme_name: "", scheme_number: "", floor_area_m2: "",
    farm_name: "", farm_portion: "", extent_ha: "",
  });
  // Step 4 — price & terms
  const [terms, setTerms] = useState({
    purchase_price: presetPrice ? Number(presetPrice) : "",
    deposit: "",
    bond_required: true,
    bond_amount: "",
    occupation_date: "",
    occupational_rent: "",
    offer_expiry_date: "",
    offer_expiry_time: "17:00",
  });
  // Step 5 — defects
  const [defects, setDefects] = useState({ structural: false, damp: false, roof: false, electrical_faults: false, other_defects: "" });

  // Step 1 — bootstrap matter on submit, then advance
  const startMatter = async (e) => {
    e?.preventDefault();
    if (!lead.name || !lead.email || !lead.phone) {
      toast.error("Full name, email and phone are required to continue.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/matter/bootstrap", {
        name: lead.name,
        email: lead.email,
        phone: lead.phone,
        matter_type: "conveyancing",
        property_value: terms.purchase_price || null,
        property_type: property.property_type,
        has_bond: terms.bond_required,
      });
      setPortal({ portal_token: data.portal_token, matter_id: data.matter_id });
      toast.success("Your matter is open — let's build your Offer to Purchase.");
      setStep(1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Persist current step's data to the portal-scoped matter
  const persistStep = async (patch) => {
    if (!portal?.portal_token) return;
    try {
      await api.patch(`/client/portal/${portal.portal_token}/otp-intake`, patch);
    } catch (err) {
      console.warn("Persist failed", err);
    }
  };

  const goNext = async () => {
    if (step === 1) {
      const persons = [
        { role: "purchaser", ...purchaser },
        { role: "seller",    ...seller },
      ];
      await persistStep({ persons });
      setStep(2);
    } else if (step === 2) {
      await persistStep({ properties: [property], otp_flags: { property_type: property.property_type } });
      setStep(3);
    } else if (step === 3) {
      const balance = (Number(terms.purchase_price) || 0) - (Number(terms.deposit) || 0);
      await persistStep({
        transaction: { ...terms, balance },
        otp_flags: { property_type: property.property_type, bond_required: !!terms.bond_required },
      });
      setStep(4);
    } else if (step === 4) {
      await persistStep({ otp: { defects } });
      setStep(5);
    }
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  // Step 6 — generate the OTP + annexures
  const generate = async () => {
    if (!portal?.portal_token) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/client/portal/${portal.portal_token}/otp-intake/generate`);
      toast.success(`OTP generated — ${data.documents.length} documents ready.`);
      setBundleReady(true);
      setDone(true);
    } catch (err) {
      const body = err?.response?.data;
      if (err?.response?.status === 422 && body?.blocked) {
        toast.error("Please complete required fields", { description: body.missing?.join(", ") });
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const downloadBundle = async () => {
    if (!portal?.portal_token) return;
    try {
      const res  = await api.get(`/client/portal/${portal.portal_token}/otp-bundle.zip`, { responseType: "blob" });
      const a    = document.createElement("a");
      a.href     = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      a.download = `EH_Inc_OTP_${portal.matter_id.slice(0, 8).toUpperCase()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const progressPct = useMemo(() => Math.round((step / (STEPS.length - 1)) * 100), [step]);

  return (
    <div className="min-h-screen flex flex-col bg-zinc-50">
      <SiteHeader />

      <div className="flex-1">
        <section className="bg-zinc-900 text-white px-6 py-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-overline text-amber-400 mb-2">Phase OTP-2 · Self-service intake</div>
            <h1 className="font-serif text-3xl md:text-5xl leading-tight">Generate your Offer to Purchase</h1>
            <p className="text-zinc-300 mt-3 max-w-2xl">
              Six guided steps. Strict template fidelity. The moment you finish, you'll have a draft OTP +
              FICA checklists ready to take to the seller — and your private portal becomes your live workspace.
            </p>

            <div className="mt-6">
              <ol className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] uppercase tracking-wider text-zinc-400">
                {STEPS.map((s) => (
                  <li key={s.idx} className={s.idx === step ? "text-amber-400" : s.idx < step ? "text-emerald-400" : ""}>
                    {s.idx + 1}. {s.label}
                  </li>
                ))}
              </ol>
              <div className="mt-2 h-1 bg-zinc-800 overflow-hidden">
                <div className="h-full bg-amber-400 transition-all" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-6 py-10">
          {/* ───── STEP 0: lead capture ───── */}
          {step === 0 && (
            <Card title="Your details" subtitle="No anonymous downloads. Tell us who you are so we can save your matter and link your draft documents." testid="otp-intake-step-0">
              <form onSubmit={startMatter} className="space-y-4">
                <Field label="Full name" required value={lead.name} onChange={(v) => setLead({ ...lead, name: v })} testid="otp-lead-name" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Email" type="email" required value={lead.email} onChange={(v) => setLead({ ...lead, email: v })} testid="otp-lead-email" />
                  <Field label="Phone" required value={lead.phone} onChange={(v) => setLead({ ...lead, phone: v })} testid="otp-lead-phone" />
                </div>
                <div className="bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>By submitting, you create a private matter file with EH Inc Attorneys. Your data is processed under POPIA. The draft OTP is for your review — it is not a substitute for legal advice.</span>
                </div>
                <Button type="submit" disabled={busy} className="w-full rounded-none bg-amber-500 text-zinc-900 hover:bg-amber-400" data-testid="otp-lead-submit">
                  {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Opening your matter…</> : <>Continue <ArrowRight className="h-4 w-4 ml-1.5" /></>}
                </Button>
              </form>
            </Card>
          )}

          {/* ───── STEP 1: parties ───── */}
          {step === 1 && (
            <Card title="Parties" subtitle="The purchaser and seller — exactly as they appear on their IDs." testid="otp-intake-step-1">
              <PartyForm label="Purchaser (you)" data={purchaser} setData={setPurchaser} prefix="purchaser" />
              <hr className="my-6 border-zinc-200" />
              <PartyForm label="Seller" data={seller} setData={setSeller} prefix="seller" />
              <NavRow onBack={goBack} onNext={goNext} backTestid="otp-back-1" nextTestid="otp-next-1" />
            </Card>
          )}

          {/* ───── STEP 2: property ───── */}
          {step === 2 && (
            <Card title="Property" subtitle="Tell us about the property you're buying." testid="otp-intake-step-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Property type</Label>
              <div className="flex gap-2 mt-1 mb-4" data-testid="otp-property-type">
                {[{ k: "freehold", l: "Freehold" }, { k: "sectional_title", l: "Sectional Title" }, { k: "agricultural", l: "Agricultural" }].map((o) => (
                  <button key={o.k} type="button" onClick={() => setProperty({ ...property, property_type: o.k })}
                    className={`px-3 py-1.5 text-xs border ${property.property_type === o.k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"}`}
                    data-testid={`otp-property-type-${o.k}`}>
                    {o.l}
                  </button>
                ))}
              </div>

              {property.property_type === "freehold" && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Erf / Portion number" value={property.erf_number} onChange={(v) => setProperty({ ...property, erf_number: v })} testid="otp-erf" />
                  <Field label="Township / Suburb" value={property.township} onChange={(v) => setProperty({ ...property, township: v })} testid="otp-township" />
                  <Field label="Province" value={property.province} onChange={(v) => setProperty({ ...property, province: v })} testid="otp-province" />
                  <Field label="Extent (m²)" type="number" value={property.extent_m2} onChange={(v) => setProperty({ ...property, extent_m2: v })} testid="otp-extent-m2" />
                  <Field label="Physical address" className="sm:col-span-2" value={property.address} onChange={(v) => setProperty({ ...property, address: v })} testid="otp-address" />
                  <Field label="Title Deed number" value={property.title_deed_number} onChange={(v) => setProperty({ ...property, title_deed_number: v })} testid="otp-tdn" />
                </div>
              )}

              {property.property_type === "sectional_title" && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Section number" value={property.section_number} onChange={(v) => setProperty({ ...property, section_number: v })} testid="otp-section" />
                  <Field label="Scheme name" value={property.scheme_name} onChange={(v) => setProperty({ ...property, scheme_name: v })} testid="otp-scheme" />
                  <Field label="Scheme number (SS…)" value={property.scheme_number} onChange={(v) => setProperty({ ...property, scheme_number: v })} testid="otp-scheme-no" />
                  <Field label="Floor area (m²)" type="number" value={property.floor_area_m2} onChange={(v) => setProperty({ ...property, floor_area_m2: v })} testid="otp-floor" />
                  <Field label="Unit address" className="sm:col-span-2" value={property.address} onChange={(v) => setProperty({ ...property, address: v })} testid="otp-address" />
                </div>
              )}

              {property.property_type === "agricultural" && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Farm name" value={property.farm_name} onChange={(v) => setProperty({ ...property, farm_name: v })} testid="otp-farm" />
                  <Field label="Farm / Portion number" value={property.farm_portion} onChange={(v) => setProperty({ ...property, farm_portion: v })} testid="otp-farm-portion" />
                  <Field label="Extent (hectares)" type="number" value={property.extent_ha} onChange={(v) => setProperty({ ...property, extent_ha: v })} testid="otp-extent-ha" />
                  <Field label="Title Deed number" value={property.title_deed_number} onChange={(v) => setProperty({ ...property, title_deed_number: v })} testid="otp-tdn" />
                </div>
              )}

              <NavRow onBack={goBack} onNext={goNext} backTestid="otp-back-2" nextTestid="otp-next-2" />
            </Card>
          )}

          {/* ───── STEP 3: price & terms ───── */}
          {step === 3 && (
            <Card title="Price and terms" subtitle="The financial structure of the offer." testid="otp-intake-step-3">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Purchase price (ZAR)" type="number" required value={terms.purchase_price}
                  onChange={(v) => setTerms({ ...terms, purchase_price: v })} testid="otp-price" />
                <Field label="Deposit (ZAR)" type="number" value={terms.deposit}
                  onChange={(v) => setTerms({ ...terms, deposit: v })} testid="otp-deposit" />
              </div>

              <Label className="text-xs uppercase tracking-wider text-zinc-500 mt-4 block">Financing</Label>
              <div className="flex gap-2 mt-1 mb-4" data-testid="otp-financing">
                {[{ k: true, l: "Bond" }, { k: false, l: "Cash" }].map((o) => (
                  <button key={String(o.k)} type="button" onClick={() => setTerms({ ...terms, bond_required: o.k })}
                    className={`px-3 py-1.5 text-xs border ${terms.bond_required === o.k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"}`}>
                    {o.l}
                  </button>
                ))}
              </div>

              {terms.bond_required && (
                <Field label="Bond amount sought (ZAR)" type="number" value={terms.bond_amount}
                  onChange={(v) => setTerms({ ...terms, bond_amount: v })} testid="otp-bond-amount" />
              )}

              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <Field label="Occupation date" type="date" required value={terms.occupation_date}
                  onChange={(v) => setTerms({ ...terms, occupation_date: v })} testid="otp-occupation-date" />
                <Field label="Occupational rent per month (ZAR)" type="number" value={terms.occupational_rent}
                  onChange={(v) => setTerms({ ...terms, occupational_rent: v })} testid="otp-occupation-rent" />
                <Field label="Offer expires (date)" type="date" value={terms.offer_expiry_date}
                  onChange={(v) => setTerms({ ...terms, offer_expiry_date: v })} testid="otp-expiry-date" />
                <Field label="Offer expires (time)" value={terms.offer_expiry_time}
                  onChange={(v) => setTerms({ ...terms, offer_expiry_time: v })} testid="otp-expiry-time" />
              </div>

              <NavRow onBack={goBack} onNext={goNext} backTestid="otp-back-3" nextTestid="otp-next-3" />
            </Card>
          )}

          {/* ───── STEP 4: defects ───── */}
          {step === 4 && (
            <Card title="Defects disclosure (optional)" subtitle="Tick anything the seller has disclosed. Skip if not applicable." testid="otp-intake-step-4">
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                {[
                  { k: "structural",         l: "Structural cracks / subsidence" },
                  { k: "damp",               l: "Damp / water ingress" },
                  { k: "roof",               l: "Roof leaks / damage" },
                  { k: "electrical_faults",  l: "Electrical faults" },
                ].map((d) => (
                  <button key={d.k} type="button"
                    onClick={() => setDefects({ ...defects, [d.k]: !defects[d.k] })}
                    className={`text-left px-3 py-2 border ${defects[d.k] ? "bg-amber-100 border-amber-400" : "bg-white border-zinc-300 hover:border-zinc-500"}`}
                    data-testid={`otp-defect-${d.k}`}>
                    {defects[d.k] ? "☒" : "☐"} {d.l}
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Other known defects</Label>
                <Textarea rows={3} className="rounded-none mt-1" value={defects.other_defects}
                  onChange={(e) => setDefects({ ...defects, other_defects: e.target.value })} data-testid="otp-other-defects" />
              </div>
              <NavRow onBack={goBack} onNext={goNext} backTestid="otp-back-4" nextTestid="otp-next-4" />
            </Card>
          )}

          {/* ───── STEP 5: generate / done ───── */}
          {step === 5 && (
            <Card title={done ? "Your OTP is ready" : "Review & generate"}
              subtitle={done ? "Download your draft and head to your private portal to continue." : "We'll generate your draft OTP + the FICA checklists for both parties."}
              testid="otp-intake-step-5">
              <SummaryRow label="Purchaser"      value={purchaser.full_name} />
              <SummaryRow label="Seller"         value={seller.full_name} />
              <SummaryRow label="Property"       value={property.address || property.erf_number || property.farm_name || "—"} />
              <SummaryRow label="Purchase price" value={terms.purchase_price ? `R ${Number(terms.purchase_price).toLocaleString("en-ZA")}` : "—"} />
              <SummaryRow label="Financing"      value={terms.bond_required ? "Bond" : "Cash"} />
              <SummaryRow label="Occupation"     value={terms.occupation_date || "—"} />

              {!done ? (
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button onClick={goBack} variant="outline" className="rounded-none" data-testid="otp-back-5">
                    <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
                  </Button>
                  <Button onClick={generate} disabled={busy} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="otp-generate-btn">
                    {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Generating…</>
                          : <><FileSignature className="h-4 w-4 mr-1.5" /> Generate my OTP draft</>}
                  </Button>
                </div>
              ) : (
                <div className="mt-6 space-y-3">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-900 text-sm">
                    <CheckCircle2 className="h-4 w-4" /> OTP drafts created — please review before signing.
                  </div>
                  <div className="flex flex-wrap gap-3 pt-2">
                    <Button onClick={downloadBundle} className="rounded-none bg-amber-500 text-zinc-900 hover:bg-amber-400" data-testid="otp-download-bundle-btn">
                      <Download className="h-4 w-4 mr-1.5" /> Download my OTP (ZIP)
                    </Button>
                    <Button variant="outline" className="rounded-none"
                      onClick={() => navigate(`/client/${portal.portal_token}`)} data-testid="otp-go-portal-btn">
                      Go to my portal →
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </section>
      </div>

      <SiteFooter />
    </div>
  );
}


// ─── helper components ────────────────────────────────────────────────

function Card({ title, subtitle, children, testid }) {
  return (
    <div className="bg-white border border-zinc-200 p-6 md:p-8" data-testid={testid}>
      <div className="text-overline text-zinc-500">{title}</div>
      <h2 className="font-serif text-2xl mt-1">{subtitle}</h2>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", className = "", required = false, testid }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wider text-zinc-500">
        {label}{required && <span className="text-amber-600"> *</span>}
      </Label>
      <Input type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="rounded-none mt-1" data-testid={testid} />
    </div>
  );
}

function PartyForm({ label, data, setData, prefix }) {
  return (
    <div>
      <div className="text-overline text-zinc-500 mb-2">{label}</div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name" required value={data.full_name} onChange={(v) => setData({ ...data, full_name: v })} testid={`otp-${prefix}-name`} />
        <Field label="ID number" required value={data.id_number} onChange={(v) => setData({ ...data, id_number: v })} testid={`otp-${prefix}-id`} />
        <Field label="Marital status" value={data.marital_status} onChange={(v) => setData({ ...data, marital_status: v })} testid={`otp-${prefix}-marital`} />
        <Field label="Phone" value={data.phone} onChange={(v) => setData({ ...data, phone: v })} testid={`otp-${prefix}-phone`} />
        <Field label="Email" type="email" value={data.email} onChange={(v) => setData({ ...data, email: v })} testid={`otp-${prefix}-email`} />
        <Field label="Residential address" className="sm:col-span-2" value={data.address} onChange={(v) => setData({ ...data, address: v })} testid={`otp-${prefix}-address`} />
      </div>
    </div>
  );
}

function NavRow({ onBack, onNext, backTestid, nextTestid }) {
  return (
    <div className="mt-6 flex justify-between gap-3">
      <Button onClick={onBack} variant="outline" className="rounded-none" data-testid={backTestid}>
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
      </Button>
      <Button onClick={onNext} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid={nextTestid}>
        Continue <ArrowRight className="h-4 w-4 ml-1.5" />
      </Button>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-zinc-100 text-sm">
      <span className="text-zinc-500">{label}</span>
      <span className="font-medium text-zinc-900 text-right">{value || "—"}</span>
    </div>
  );
}
